import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { saveAs } from 'file-saver';
import './App.css';

// Hooks
import { useAuth } from './hooks/useAuth';
import { useInterests } from './hooks/useInterests';
import { useModules } from './hooks/useModules';
import { useChat } from './hooks/useChat';
import { useAdmin } from './hooks/useAdmin';
import { useIntro } from './hooks/useIntro';
import { useModuleInfo } from './hooks/useModuleInfo';
import { useActiveModules } from './hooks/useActiveModules';

// Components
import Header from './components/Shared/Header';
import Logo from './components/Shared/Logo';
import ConfirmationModal from './components/Shared/ConfirmationModal';
import LoginForm from './components/Auth/LoginForm';
import RegisterForm from './components/Auth/RegisterForm';
import ForgotPasswordForm from './components/Auth/ForgotPasswordForm';
import TermsModal from './components/Auth/TermsModal';
import ModuleSelection from './components/Modules/ModuleSelection';
import ModuleInfoModal from './components/Modules/ModuleInfoModal';
import ChatBox from './components/Chat/ChatBox';
import AdminDashboard from './components/Admin/AdminDashboard';
import SystemAdminPage from './components/SystemAdmin/SystemAdminPage';
import PromptManagementPage from './components/PromptEngineer/PromptManagementPage';
import Footer from './components/Shared/Footer';
import IntroQuiz from './components/Shared/IntroQuiz';


// Utils
import { getFilteredAndSortedStudents, generateCSVContent } from './utils/adminHelpers';

const App = () => {
  // Initialize hooks
  const auth = useAuth();
  const interests = useInterests(auth.userId);
  const modules = useModules(auth.userId);
  const chat = useChat();
  const admin = useAdmin();
  const introProps = useIntro();
  const moduleInfoProps = useModuleInfo();

  // UI States
  const [showLogo, setShowLogo] = useState(true);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showExitConfirmation, setShowExitConfirmation] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [showNoInterestMessage, setShowNoInterestMessage] = useState(false);
  const [fadeOut, setFadeOut] = useState(false);
  
  // Concept sidebar
  const [selectedConcept, setSelectedConcept] = useState(null);
  const conceptDetailsRef = useRef(null);
  const dropdownRef = useRef(null);

  const isInChatSession = modules.selectedModule && !modules.showModuleInfo;
  const { activeModules, activeModulesLoading } = useActiveModules();

  // Handle email verification on mount
  useEffect(() => {
    const verifyEmail = async () => {
      const params = new URLSearchParams(window.location.search);
      const token = params.get("confirmation_token");

      if (token) {
        try {
          window.location.href = `${process.env.REACT_APP_API_URL}/api/verify_email?confirmation_token=${token}`;
        } catch (err) {
          console.error("Email verification failed:", err);
        }
      }
    };

    verifyEmail();
  }, []);

  // Handle payment success on mount
  useEffect(() => {
    async function finalizePayment() {
      const userEmail = localStorage.getItem('payment_email');
      const stripeSessionId = localStorage.getItem('stripe_session_id');

      if (!userEmail || !stripeSessionId) return;

      try {
        const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/payment_success`, {
          user_email: userEmail,
          stripe_sessionId: stripeSessionId,
        });

        const data = response.data;

        auth.setUserId(data.user_id);
        auth.setUserType(data.user_type);
        modules.setCompletedModules(data.completed_modules);
        modules.setAccessibleModules(data.accessible_modules);
        interests.setUserInterests(data.interests);
        modules.setPassageCounts(data.passage_counts || {});
        admin.setAdminView(data.admin_view);
        admin.setStudentInfoAdminDict(data.student_info_admin_dict);
        interests.setTopUserInterests(data.top_user_interests);
        interests.setLatestPassageSubtopics(data.latest_passage_subtopics);

        auth.setFirstName(data.user_first_name);
        auth.setLastName(data.user_last_name);
        interests.setUserInterestInput('');
        auth.setErrorMessage('');
        auth.setEmail('');
        auth.setRegisterPassword('');
        auth.setConfirmRegisterPassword('');
        setShowLogo(false);

        localStorage.removeItem('payment_email');
        localStorage.removeItem('stripe_session_id');
      } catch (err) {
        auth.setErrorMessage('');
      }
    }

    finalizePayment();
  }, []);

  // Handle clicking outside dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        admin.setShowDropdown(false);
      }
    }

    if (admin.showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [admin.showDropdown]);

  // Handle browser navigation warnings
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isInChatSession) {
        e.preventDefault();
        e.returnValue = '';
        setPendingAction('signout');
        return '';
      }
    };

    const handlePopState = (e) => {
      if (isInChatSession) {
        window.history.pushState(null, '', window.location.href);
        setShowExitConfirmation(true);
        setPendingAction('back');
      }
    };

    window.history.pushState(null, '', window.location.href);

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isInChatSession]);

  // Reset scroll when concept changes
  useEffect(() => {
    if (conceptDetailsRef.current) {
      conceptDetailsRef.current.scrollTop = 0;
    }
  }, [selectedConcept]);

  // Handlers
  const handleLogin = async () => {
    const result = await auth.login();
    if (result.success) {
      auth.setUserId(result.data.user_id);
      auth.setUserType(result.data.user_type);
      modules.setCompletedModules(result.data.completed_modules);
      modules.setAccessibleModules(result.data.accessible_modules);
      interests.setUserInterests(result.data.interests);
      modules.setPassageCounts(result.data.passage_counts || {});
      admin.setAdminView(result.data.admin_view);
      admin.setStudentInfoAdminDict(result.data.student_info_admin_dict);
      interests.setTopUserInterests(result.data.top_user_interests);
      interests.setLatestPassageSubtopics(result.data.latest_passage_subtopics);
      interests.setUserInterestInput('');
      introProps.checkIntroStatus(result.data.done_intro)
      auth.setErrorMessage('');
      auth.setFirstName(result.data.user_first_name);
      auth.setLastName(result.data.user_last_name);
      auth.setEmail('');
      auth.setRegisterPassword('');
      auth.setConfirmRegisterPassword('');
      setShowLogo(false);
    }
  };

  const handleModuleSelect = async (moduleId) => {
    if (interests.userInterests.length === 0) {
      setShowNoInterestMessage(true);
      return;
    }

    modules.setSelectedModule(moduleId);

    const result = await modules.getPassageSubtopics();
    if (!result.success) {
      auth.setErrorMessage("Could not generate subtopics.");
    }

    // Fetch module details and show info modal
    const res =  await moduleInfoProps.fetchModuleDetails(moduleId);
    if (!res.success) {
      auth.setErrorMessage("Could not get module info details.");
    }
  };

  const handleSubtopicSelection = async (subtopic) => {
    modules.setShowModuleInfo(true);
    modules.setShowSubtopicModal(false);
    modules.setIsFirstMessageReady(false);
    // Store selected subtopic
    modules.setSelectedSubtopic(subtopic);

    // START TUTOR HERE instead of in modal
    const result = await chat.initModuleChat(
     auth.userId,
     chat.sessionId,
     modules.selectedModule,
     subtopic
    );
 
    if (result.success) {
     auth.setUserId(result.data.user_id);
     chat.setSessionId(result.data.session_id);
     modules.setCompletedModules(result.data.completed_modules);
     modules.setPassageCounts(result.data.passage_counts || {});
     modules.setIsFirstMessageReady(true);
     setSelectedConcept(moduleInfoProps.concepts[0]) // reset to first concept 
    } else {
     auth.setErrorMessage("Failed to start module.");
    }

  };

  const handleStudentSelect = async(studentId) => {
    admin.setSelectedStudent(studentId);
    admin.setExpandedSessions({});
    // Fetch student details
    const res =  await admin.fetchSessions(studentId);
    if (!res.success) {
      auth.setErrorMessage("Could not get sessions for student.");
    }
  }

  // Handle video click in module info modal
  const handleVideoClick = async () => {
    if (moduleInfoProps.selectedModuleData?.video_filename) {
      await moduleInfoProps.fetchModuleQuestions(
        modules.selectedModule
      );
    }
  };

  const handleSendMessage = async () => {
    if (!chat.userMessage) return;
    const result = await chat.sendMessage(auth.userId, modules.selectedModule);
    
    if (result.exitMessage) {
      modules.setCompletedModules(result.data.completed_modules);
      modules.setAccessibleModules(result.data.accessible_modules);
      modules.setPassageCounts(result.data.passage_counts || {});
    }
  };

  const handleBeginDialogue = () => {
    // Close the module info modal
    moduleInfoProps.closeModuleInfoModal();
    modules.setShowModuleInfo(false);
    chat.setStartTime(Date.now());
    chat.setElapsedTime(0);
  };

  const handlePerformanceSummaryExit = async () => {
    modules.setSelectedModule(null);
    chat.resetChat();
    setFadeOut(false);
  // Re-fetch module data to get updated accessible/completed modules
    await modules.fetchModuleData(auth.userId);
  };

  const handleSignOut = () => {
    if (conceptDetailsRef.current) {
      conceptDetailsRef.current.scrollTop = 0;
    }

    auth.signOut();
    chat.resetChat();
    modules.setSelectedModule(null);
    modules.setShowSubtopicModal(false);
    modules.setSubtopics([]);
    modules.setShowQuizModal(false);
    modules.setQuestions([]);
    modules.abortOngoingRequests();
    admin.resetAdmin();
    setShowLogo(true);
    interests.setInterestErrorMessage('');
  };

  const handleBack = () => {
    if (conceptDetailsRef.current) {
      conceptDetailsRef.current.scrollTop = 0;
    }

    modules.setSelectedModule(null);
    modules.abortOngoingRequests();
    chat.resetChat();
    interests.setInterestErrorMessage('');
    modules.setShowSubtopicModal(false);
    modules.setSubtopics([]);
    modules.setShowQuizModal(false);
    modules.setQuestions([]);
  };

  const handleToggleAdminView = () => {
    if (conceptDetailsRef.current) {
      conceptDetailsRef.current.scrollTop = 0;
    }

    admin.setAdminView(!admin.adminView);
    modules.setSelectedModule(null);
    modules.abortOngoingRequests();
    chat.resetChat();
    admin.setSelectedStudent(null);
    admin.setExpandedSessions({});
    admin.setStudentSearch("");
    admin.setCompletedModuleFilters([]);
    interests.setInterestErrorMessage('');
    modules.setShowSubtopicModal(false);
    modules.setSubtopics([]);
    modules.setShowQuizModal(false);
    modules.setQuestions([]);
  };

  const confirmExit = () => {
    setShowExitConfirmation(false);
    if (pendingAction === 'signout') {
      handleSignOut();
    } else if (pendingAction === 'back') {
      handleBack();
    } else if (pendingAction === 'backadmin') {
      handleToggleAdminView();
    }
    setPendingAction(null);
  };

  const cancelExit = () => {
    setShowExitConfirmation(false);
    setPendingAction(null);
  };

  const handleExitAction = (action) => {
    if (isInChatSession) {
      setShowExitConfirmation(true);
      setPendingAction(action);
    } else {
      if (action === 'signout') handleSignOut();
      else if (action === 'back') handleBack();
      else if (action === 'backadmin') handleToggleAdminView();
    }
  };

  const downloadCSV = () => {
    const filteredStudents = getFilteredAndSortedStudents(
      admin.studentInfoAdminDict,
      admin.studentSearch,
      admin.completedModuleFilters,
      admin.sortConfig
    );
    if (activeModulesLoading || !activeModules) {
      console.log('Active Modules not loaded yet');
      return;
    }
    
    const csvContent = generateCSVContent(filteredStudents, activeModules);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, 'student_data.csv');
  };

  const handleIntroComplete = async () => {
    await introProps.completeIntro(auth.userId);
  };

  // Render
  return (
    <div className="chat-container">
      <Header
        userId={auth.userId}
        userType={auth.userType}
        onSignOut={() => handleExitAction('signout')}
        onToggleAdminView={() => handleExitAction('backadmin')}
        adminView={admin.adminView}
        selectedModule={modules.selectedModule}
        showSubtopicModal={modules.showSubtopicModal}
        onBack={() => handleExitAction('back')}
        isInChatSession={isInChatSession}
      />

      <Logo show={showLogo} />

      {/* System admin gets a dedicated page */}
      {auth.userId && auth.userType === 'admin' ? (
        <SystemAdminPage callerUserId={auth.userId} />
      ) : auth.userId && auth.userType === 'promptengineer' ? (
        <PromptManagementPage callerUserId={auth.userId} />
      ) : admin.adminView && auth.userType === 'supervisor' ? (
        <AdminDashboard
          studentInfoAdminDict={admin.studentInfoAdminDict}
          studentSessionsDict={admin.studentSessionsDict}
          studentSearch={admin.studentSearch}
          setStudentSearch={admin.setStudentSearch}
          completedModuleFilters={admin.completedModuleFilters}
          setCompletedModuleFilters={admin.setCompletedModuleFilters}
          showDropdown={admin.showDropdown}
          setShowDropdown={admin.setShowDropdown}
          dropdownRef={dropdownRef}
          sortConfig={admin.sortConfig}
          handleSort={admin.handleSort}
          handleStudentSelection={handleStudentSelect}
          selectedStudent={admin.selectedStudent}
          setSelectedStudent={admin.setSelectedStudent}
          expandedSessions={admin.expandedSessions}
          toggleSession={admin.toggleSession}
          onDownloadCSV={downloadCSV}
          getFilteredAndSortedStudents={() =>
            getFilteredAndSortedStudents(
              admin.studentInfoAdminDict,
              admin.studentSearch,
              admin.completedModuleFilters,
              admin.sortConfig
            )
          }
        />
      ) : (
        // Regular App View Section
        <>
          {!auth.userId ? (
            auth.showRegister ? (
              <>
                <RegisterForm
                  firstName={auth.firstName}
                  setFirstName={auth.setFirstName}
                  lastName={auth.lastName}
                  setLastName={auth.setLastName}
                  email={auth.email}
                  setEmail={auth.setEmail}
                  registerPassword={auth.registerPassword}
                  setRegisterPassword={auth.setRegisterPassword}
                  confirmRegisterPassword={auth.confirmRegisterPassword}
                  setConfirmRegisterPassword={auth.setConfirmRegisterPassword}
                  agreedToTerms={auth.agreedToTerms}
                  onRegister={auth.register}
                  onBack={() => {
                    auth.setShowRegister(false);
                    auth.setErrorMessage('');
                    auth.setFirstName('');
                    auth.setLastName('');
                    auth.setEmail('');
                    auth.setRegisterPassword('');
                    auth.setConfirmRegisterPassword('');
                    setShowLogo(true);
                  }}
                  onShowTerms={() => setShowTermsModal(true)}
                  errorMessage={auth.errorMessage}
                  registrationInProgress={auth.registrationInProgress}
                />
                {showTermsModal && (
                  <TermsModal
                    onClose={() => setShowTermsModal(false)}
                    onAgree={() => {
                      auth.setAgreedToTerms(true);
                      setShowTermsModal(false);
                    }}
                  />
                )}
              </>
            ) : auth.showForgotPassword ? (
              <ForgotPasswordForm
                showNewPasswordForm={auth.showNewPasswordForm}
                resetEmail={auth.resetEmail}
                setResetEmail={auth.setResetEmail}
                newPassword={auth.newPassword}
                setNewPassword={auth.setNewPassword}
                confirmNewPassword={auth.confirmNewPassword}
                setConfirmNewPassword={auth.setConfirmNewPassword}
                onResetPassword={auth.resetPassword}
                onSubmitNewPassword={auth.submitNewPassword}
                onBack={() => {
                  auth.setShowForgotPassword(false);
                  auth.setErrorMessage('');
                }}
                resetCooldown={auth.resetCooldown}
                isSending={auth.isSending}
                errorMessage={auth.errorMessage}
                handleInputChange={() => auth.setErrorMessage('')}
              />
            ) : (
              <LoginForm
                userName={auth.userName}
                setUserName={auth.setUserName}
                userPassword={auth.userPassword}
                setUserPassword={auth.setUserPassword}
                onLogin={handleLogin}
                onForgotPassword={() => {
                  auth.setErrorMessage('');
                  auth.setResetEmail('');
                  auth.setUserName('');
                  auth.setUserPassword('');
                  auth.setShowForgotPassword(true);
                }}
                onRegister={() => {
                  auth.setShowRegister(true);
                  auth.setErrorMessage('');
                  auth.setUserName('');
                  auth.setUserPassword('');
                  setShowLogo(false);
                  auth.setAgreedToTerms(false);
                }}
                isSending={auth.isSending}
                errorMessage={auth.errorMessage}
                handleInputChange={() => auth.setErrorMessage('')}
              />
              )
          ) : introProps.hasCompletedIntro === null ? (
              <div className="loading">Checking intro status...</div>
          ) : !introProps.hasCompletedIntro && introProps.showIntroQuiz ? (
              <IntroQuiz
                   isLoading={introProps.isLoadingIntro}
                   questions={introProps.introQuestions}
                   videoFileName={introProps.introVideoFileName}
                   onComplete={handleIntroComplete}
              />
          ): !modules.selectedModule || modules.showSubtopicModal? (
            <ModuleSelection
              userId={auth.userId}
              userInterests={interests.userInterests}
              userInterestInput={interests.userInterestInput}
              setUserInterestInput={interests.setUserInterestInput}
              interestErrorMessage={interests.interestErrorMessage}
              topUserInterests={interests.topUserInterests}
              latestPassageSubtopics={interests.latestPassageSubtopics}
              onSubmitInterest={interests.submitInterest}
              onRemoveInterest={interests.removeInterest}
              accessibleModules={modules.accessibleModules}
              completedModules={modules.completedModules}
              passageCounts={modules.passageCounts}
              onModuleSelect={handleModuleSelect}
              showSubtopicModal={modules.showSubtopicModal}
              isLoadingSubtopics={modules.isLoadingSubtopics}
              subtopics={modules.subtopics}
              randomUserInterest={modules.randomUserInterest}
              onSubtopicSelection={handleSubtopicSelection}
              onCloseSubtopicModal={() => {
                modules.setShowSubtopicModal(false);
                modules.setSelectedModule(null);
                modules.setSubtopics([]);
                modules.setIsModuleLoading(false);
              }}
              showNoInterestMessage={showNoInterestMessage}
              setShowNoInterestMessage={setShowNoInterestMessage}
              errorMessage={auth.errorMessage}
            />
          ) : modules.showModuleInfo ? (

            <ModuleInfoModal
              moduleData={moduleInfoProps.selectedModuleData}
              infoTitle={moduleInfoProps.infoTitle}
              infoMessages={moduleInfoProps.infoMessages}
              isFirstMessageReady={modules.isFirstMessageReady}
              onFetchQuestions={handleVideoClick}
              questions={moduleInfoProps.moduleQuizQuestions}
              isLoadingQuestions={moduleInfoProps.isLoadingModuleQuiz}
              onClose={() => {
                moduleInfoProps.closeModuleInfoModal();
                modules.setShowModuleInfo(false);
              }}
              onBeginDialogue={handleBeginDialogue}
            />
          ) : (

            <ChatBox
              chatMessages={chat.chatMessages}
              firstAssistantMessage={chat.firstAssistantMessage}
              hasEnoughMessages={chat.hasEnoughMessages}
              showFirstMessageModal={chat.showFirstMessageModal}
              setShowFirstMessageModal={chat.setShowFirstMessageModal}
              userMessage={chat.userMessage}
              setUserMessage={chat.setUserMessage}
              onSendMessage={handleSendMessage}
              isSending={chat.isSending}
              elapsedTime={chat.elapsedTime}
              startTime={chat.startTime}
              getProgressPercent={chat.getProgressPercent}
              messagesEndRef={chat.messagesEndRef}
              selectedConcept={selectedConcept}
              setSelectedConcept={setSelectedConcept}
              moduleId={chat.moduleId}
              concepts={moduleInfoProps.concepts}
              conceptDetailsRef={conceptDetailsRef}
              isGeneratingPerformanceSummary={chat.isGeneratingPerformanceSummary}
              showCompleteOverlay={chat.showCompleteOverlay}
              performanceSummary={chat.performanceSummary}
              lastAssistantMessage={chat.lastAssistantMessage}  
              onPerformanceSummaryExit={handlePerformanceSummaryExit}
              fadeOut={fadeOut}
            />
          )}
        </>
      )}

      {showExitConfirmation && (
        <ConfirmationModal onConfirm={confirmExit} onCancel={cancelExit} />
      )}

      <Footer />
    </div>
  );
};

export default App;
